import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import EditIcon from 'components/Icons/EditIcon';
import { colors } from 'theme';
import Dialog from 'components/Dialog';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import PageContent from 'components/Generic/PageContent';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { DialogWidth, ProductionForm as ProductionFormType } from 'types';
import { formatProductionBeforePut, formatProductionForEdit } from 'utils/normalizer';
import { useProductionById } from 'hooks';
import { putProduction } from 'services/apiOperations';
import ProductionForm from 'components/Forms/ProductionForm';
import Loading from 'components/Loading';
import { goBack } from 'utils/history';

type SubmissionError = {
  status: number;
  response: {
    body: {
      violations?: {
        code: string;
        message: string;
        propertyPath: string;
      }[];
    };
  };
};

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: '0',
  },
  buttonBar: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      margin: '0 10px',
    },
  },
});

const ProductionEdit: React.FC = () => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { productionId } = useParams<{ productionId: string }>();
  const location = useLocation<{ referer?: string }>();
  const backRoute = location.state?.referer ?? generatePath(Routes.ProductionShow, { productionId });
  const { loading, value: production } = useProductionById(productionId);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const [duplicateMaterials, setDuplicateMaterials] = useState<string>('');
  const classes = useStyles();

  const onSubmit = async (production: ProductionFormType) => {
    if (isSubmitting || !sortingCenter) {
      return;
    }
    const formattedProduction = formatProductionBeforePut(production, sortingCenter['@id']);
    setIsSubmitting(true);
    putProduction(formattedProduction, productionId)
      .then(() => {
        notify({
          message: t('productionForm.edition.success'),
          severity: 'success',
        });
        goBack(backRoute, history);
        setIsSubmitting(false);
      })
      .catch((e: SubmissionError) => {
        if (e.status === 412) {
          if (e.response.body.violations) {
            let violations = e.response.body.violations;
            const duplicates = violations.filter(violation => violation.code === 'DUPLICATE_PRODUCTIONS');
            if (duplicates.length === 1) {
              const duplicateMaterialMessages = duplicates.map(material => material.message.toLowerCase()).join('');
              setDuplicateMaterials(duplicateMaterialMessages);
            } else if (duplicates.length > 1) {
              const duplicateMaterialMessages = duplicates.map(material => '- ' + material.message).join('\n ');
              setDuplicateMaterials(duplicateMaterialMessages);
            }

            setDuplicateModalOpen(true);
            setIsSubmitting(false);
            return;
          }
        }
      });
  };

  return (
    <>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDuplicateModalOpen}
        title={
          duplicateMaterials.includes('-')
            ? t('productionForm.create.duplicateModal.titleMultiple', { materials: duplicateMaterials })
            : t('productionForm.create.duplicateModal.title', { materials: duplicateMaterials })
        }
        width={DialogWidth.DEFAULT}
        renderContent={() => (
          <>
            <div className={classes.buttonBar}>
              <Button
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                onClick={() => {
                  goBack(Routes.Productions, history);
                }}
              >
                {t('productionForm.create.duplicateModal.backToList')}
              </Button>
              <Button
                endIcon={<EditIcon />}
                variant="outlined"
                onClick={() => {
                  setDuplicateModalOpen(false);
                }}
                style={{
                  background: colors.grey900,
                  color: 'white',
                }}
              >
                {t('productionForm.create.duplicateModal.edit')}
              </Button>
            </div>
          </>
        )}
      />
      <PageContent title={t('productions.edit.title')} backRoute={backRoute} withButtonBar>
        {loading ? (
          <Loading />
        ) : (
          production && <ProductionForm onSubmit={onSubmit} productions={formatProductionForEdit(production)} />
        )}
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        formName="production_form"
        previousStepLabel={t('productions.edit.cancelButtonLabel')}
        onClickPrevious={() => {
          goBack(backRoute, history);
        }}
        disableNext={isSubmitting}
        nextStepIcon={<CheckCircleOutlineOutlinedIcon />}
        nextStepLabel={t('productions.edit.editButtonLabel')}
      />
    </>
  );
};
export default ProductionEdit;
